import React, { ReactElement, useContext, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { Box, Typography, Button, Switch } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ReferralInvitationCard from '../../../uiParts/card/ReferralInvitationCard';
import { white, gray100, gray40, gray80, orange30 } from '../../../../constants/colors';
import { SNACKBAR_AUTO_HIDE_DURATION_SHORT } from '../../../../constants/basic';
import { useProxyAccessControl } from '../../../../hooks/useProxyAccessControl';
import { isOperatorStaff } from '../../../../utils/permission';
import { useCompanyId } from '../../../../hooks/useCompanyId';
import {
  PROXY_RESTRICTED_OPERATION_MESSAGE,
  ACCOUNT_MANAGEMENT_TITLE,
  OCR_USAGE_MONTHLY_LABEL,
  OCR_USAGE_MONTHLY_USAGE_LABEL,
  OCR_USAGE_CREDIT_LABEL,
} from '../../../../constants/labels';
import { buildOcrUsageDisplay } from '../../../../utils/ocrUsageDisplay';
import { HELP_FAQ_OCR_USAGE } from '../../../../constants/externalUrls';
import {
  boldXxl,
  spHeaderTitle,
  regularMd,
  regularSm,
  sectionTitle,
  infoLabel,
  infoValue,
  infoRowActionButtonWidth,
} from '../../../../constants/location';
import SideBar from '../../../uiParts/menu/SideBar';
import { usePageLayout } from '../../../../hooks/usePageLayout';
import MetaTitle from '../../../utils/MetaTitle';
import AccountManagementDialog from '../../../uiParts/modal/AccountManagementDialog';
import { AuthContext } from '../../../../contexts/auth';
import { getCompanyForAccount } from '../../../../api/firebase/firestore/company/read';
import { updateCompanyAiLearning } from '../../../../api/firebase/firestore/company/update';
import { getUser } from '../../../../api/firebase/firestore/user/read';
import {
  getEarliestExpiringCredit,
  hasOcrCreditGrants,
} from '../../../../api/firebase/firestore/ocrCreditUsage/read';
import type { CompanyAccountType } from '../../../../types/company';
import { companyPlan, paymentCycle, userRole } from '../../../../constants/enum';
import { getPlanLabel } from '../../../../constants/plans';
import CustomLoading from '../../../uiParts/form/CustomLoading';
import CustomSnackbar from '../../../uiParts/form/CustomSnackbar';
import OcrCreditUsageHistoryModal from '../../../uiParts/modal/OcrCreditUsageHistoryModal';
import MfaSettingRow from '../../../uiParts/mfa/MfaSettingRow';

const styles = {
  headerTitle: {
    ...boldXxl,
    color: gray100,
    '@media screen and (max-width:960px)': {
      ...spHeaderTitle,
    },
  },
  section: {
    border: `1px solid ${gray40}`,
    borderRadius: '4px',
    backgroundColor: white,
    padding: '32px',
    margin: '0 32px 16px',
    width: 'calc(100% - 64px)',
    boxSizing: 'border-box',
    '@media screen and (max-width:960px)': {
      padding: '16px',
    },
  },
  sectionTitle: {
    ...sectionTitle,
    color: gray100,
    marginBottom: '24px',
    paddingLeft: '12px',
    borderLeft: '3px solid #F03C0A',
    display: 'flex',
    alignItems: 'center',
  },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 0',
    '@media screen and (max-width:960px)': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '4px',
    },
  },
  infoLabel: {
    ...infoLabel,
    minWidth: '120px',
    flexShrink: 0,
    lineHeight: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginRight: '69px',
    '@media screen and (max-width:960px)': {
      marginRight: 0,
      marginBottom: '4px',
    },
  },
  infoValue: {
    ...infoValue,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    flex: 1,
    '@media screen and (max-width:960px)': {
      width: '100%',
    },
  },
  infoValueText: {
    ...infoValue,
    lineHeight: '24px',
  },
  passwordDisplay: {
    ...regularMd,
    color: gray100,
    fontSize: '20px',
    letterSpacing: '4px',
  },
  infoValueColumn: {
    ...regularMd,
    color: gray100,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1,
    alignItems: 'flex-start',
    '@media screen and (max-width:960px)': {
      width: '100%',
    },
  },
  changeButton: {
    '&:hover': {
      backgroundColor: white,
      border: `1px solid #F03C0A`,
      boxShadow: 'none',
      color: '#F03C0A',
    },
    backgroundColor: white,
    border: `1px solid #F03C0A`,
    boxShadow: 'none',
    color: '#F03C0A',
    height: '32px',
    padding: '8px 16px',
    fontSize: '14px',
    minWidth: '80px',
  },
  editButton: {
    '&:hover': {
      backgroundColor: white,
      border: `1px solid ${gray80}`,
      boxShadow: 'none',
      color: gray80,
    },
    backgroundColor: white,
    border: `1px solid ${gray80}`,
    boxShadow: 'none',
    color: gray80,
    height: '32px',
    padding: '8px 16px',
    fontSize: '14px',
    minWidth: '80px',
  },
  switchRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '12px 0',
  },
  switchLabel: {
    ...regularMd,
    color: gray100,
  },
  switchDescription: {
    ...regularSm,
    color: gray80,
    marginTop: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    alignItems: 'flex-start',
  },
  withdrawLink: {
    ...regularSm,
    color: '#F03C0A',
    textDecoration: 'underline',
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.8,
    },
  },
  editButtonWithMargin: {
    '&:hover': {
      backgroundColor: white,
      border: `1px solid ${gray80}`,
      boxShadow: 'none',
      color: gray80,
    },
    backgroundColor: white,
    border: `1px solid ${gray80}`,
    boxShadow: 'none',
    color: gray80,
    height: '32px',
    padding: '8px 16px',
    fontSize: '14px',
    minWidth: infoRowActionButtonWidth,
    width: infoRowActionButtonWidth,
  },
  infoValueWithGap86: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '86px',
    flex: 1,
    '@media screen and (max-width:960px)': {
      gap: '16px',
      width: '100%',
    },
  },
  infoValueSpaceBetween: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flex: 1,
    justifyContent: 'space-between',
    '@media screen and (max-width:960px)': {
      width: '100%',
    },
  },
  infoRowFlexStart: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '16px 0',
    '@media screen and (max-width:960px)': {
      flexDirection: 'column',
      gap: '4px',
    },
  },
  ocrRow: {
    display: 'flex',
    alignItems: 'center',
    '@media screen and (max-width:960px)': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '2px',
    },
  },
  ocrRowCredit: {
    display: 'flex',
    alignItems: 'center',
    '@media screen and (max-width:960px)': {
      flexWrap: 'wrap',
      gap: '4px',
    },
  },
  ocrLabel: {
    minWidth: '150px',
    textAlign: 'left',
    '@media screen and (max-width:960px)': {
      minWidth: 'auto',
    },
  },
  ocrValue: {
    textAlign: 'left',
    flex: 1,
  },
  switchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flex: 1,
    '@media screen and (max-width:960px)': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '8px',
    },
  },
  switchWithLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  switchStateText: {
    ...regularMd,
    color: gray100,
    minWidth: '40px',
  },
  switchDescriptionPadding: {
    ...regularSm,
    color: gray80,
    marginTop: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    alignItems: 'flex-start',
    paddingLeft: 0,
    '@media screen and (max-width:960px)': {
      overflowX: 'scroll',
      paddingBottom: '8px',
    },
  },
  withdrawContainer: {
    margin: '0 16px 16px',
    textAlign: 'center',
    '@media screen and (max-width:960px)': {
      margin: '0 16px 8px',
    },
  },
  helpIcon: {
    fontSize: '16px',
    color: gray80,
    cursor: 'pointer',
  },
  addButton: {
    '&:hover': {
      backgroundColor: white,
      border: `1px solid #F03C0A`,
      boxShadow: 'none',
      color: '#F03C0A',
    },
    backgroundColor: white,
    border: `1px solid #F03C0A`,
    boxShadow: 'none',
    color: '#F03C0A',
    height: '32px',
    padding: '8px 16px',
    fontSize: '14px',
    minWidth: '80px',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  ocrCreditRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    width: '100%',
  },
  expiryLink: {
    ...regularSm,
    color: '#F03C0A',
    cursor: 'pointer',
    textDecoration: 'underline',
    // 金額と地続きに読めないよう十分に離す（#3146。「残高 7,350円有効期限:」と繋がって見えるのを防ぐ）
    marginLeft: '16px',
    '&:hover': {
      opacity: 0.8,
    },
  },
};

// プラン名を取得
const getPlanName = (plan: number): string => {
  return getPlanLabel(plan);
};

// 支払いサイクル名を取得
const getPaymentCycleName = (cycle: number): string => {
  switch (cycle) {
    case paymentCycle.none:
      return '支払いなし';
    case paymentCycle.monthly:
      return '月払い';
    case paymentCycle.yearly:
      return '年払い';
    default:
      return '不明';
  }
};

// 日付フォーマット（yyyy/mm/dd）
const formatDate = (date: Date): string => {
  return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
};

const CompanyAccount = (): ReactElement => {
  const navigate = useNavigate();
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();
  const { user } = useContext(AuthContext);

  // 代理モード中の制限パスチェック
  useProxyAccessControl();
  const { companyId: effectiveCompanyId, isProxyMode } = useCompanyId();

  const [companyData, setCompanyData] = useState<CompanyAccountType | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [modelImprovement, setModelImprovement] = useState<boolean>(true);
  const [planChangeDialogOpen, setPlanChangeDialogOpen] = useState<boolean>(false);
  const [userCountChangeDialogOpen, setUserCountChangeDialogOpen] = useState<boolean>(false);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState<boolean>(false);
  const [ocrCreditDialogOpen, setOcrCreditDialogOpen] = useState<boolean>(false);
  const [expiringCredit, setExpiringCredit] = useState<{
    expiresAt: Date;
    totalAmount: number;
  } | null>(null);
  const [hasCreditGrants, setHasCreditGrants] = useState<boolean>(false);
  const [usageHistoryModalOpen, setUsageHistoryModalOpen] = useState<boolean>(false);

  // データ取得
  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (!user?.uid || !effectiveCompanyId) {
        setIsLoading(false);
        return;
      }

      try {
        // 企業情報を取得
        const company = await getCompanyForAccount(effectiveCompanyId);
        if (company) {
          setCompanyData(company);
          setModelImprovement(company.allow_ai_learning);

          // 有効期限情報・付与履歴の有無を取得（エラーでも企業情報は表示する）
          try {
            const [expiringCreditData, hasGrants] = await Promise.all([
              getEarliestExpiringCredit(effectiveCompanyId),
              hasOcrCreditGrants(effectiveCompanyId),
            ]);
            setExpiringCredit(expiringCreditData);
            setHasCreditGrants(hasGrants);
          } catch (expiryError) {
            Sentry.captureException(expiryError);
          }
        }

        // ユーザー情報を取得（メールアドレス）
        if (!isProxyMode) {
          const userData = await getUser(user.uid);
          if (userData?.email) {
            setUserEmail(userData.email);
          }
        }
      } catch (error) {
        setErrorMessage('企業情報が取得できませんでした');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [user, effectiveCompanyId, isProxyMode]);

  // パスワード変更ボタンのクリックハンドラー
  const handlePasswordChange = (): void => {
    navigate('/auth/password_change');
  };

  // 会社情報編集ボタンのクリックハンドラー
  const handleCompanyEdit = (): void => {
    if (isProxyMode) {
      setErrorMessage(PROXY_RESTRICTED_OPERATION_MESSAGE);
      return;
    }
    navigate('/company/edit');
  };

  // モデル改善スイッチのハンドラー
  const handleModelImprovementToggle = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const newValue = event.target.checked;
    const previousValue = modelImprovement;

    // 楽観的更新
    setModelImprovement(newValue);

    try {
      if (!user?.uid || !effectiveCompanyId) {
        throw new Error('ユーザー情報が取得できませんでした');
      }

      // Firestoreを更新
      await updateCompanyAiLearning(effectiveCompanyId, newValue, user.uid);

      // 成功メッセージ表示
      setSuccessMessage('設定を保存しました');
    } catch (error) {
      // エラー時は元の状態に戻す
      setModelImprovement(previousValue);
      setErrorMessage('設定の保存に失敗しました。');
    }
  };

  // プラン変更ダイアログ
  const handlePlanChangeOpen = (): void => {
    if (isProxyMode) {
      setErrorMessage(PROXY_RESTRICTED_OPERATION_MESSAGE);
      return;
    }
    setPlanChangeDialogOpen(true);
  };

  const handlePlanChangeClose = (): void => {
    setPlanChangeDialogOpen(false);
  };

  // ユーザー数変更ダイアログ
  const handleUserCountChangeOpen = (): void => {
    if (isProxyMode) {
      setErrorMessage(PROXY_RESTRICTED_OPERATION_MESSAGE);
      return;
    }
    setUserCountChangeDialogOpen(true);
  };

  const handleUserCountChangeClose = (): void => {
    setUserCountChangeDialogOpen(false);
  };

  // 退会ダイアログ
  const handleWithdrawOpen = (): void => {
    if (isProxyMode) {
      setErrorMessage(PROXY_RESTRICTED_OPERATION_MESSAGE);
      return;
    }
    setWithdrawDialogOpen(true);
  };

  const handleWithdrawClose = (): void => {
    setWithdrawDialogOpen(false);
  };

  // OCRクレジット追加ダイアログ
  const handleOcrCreditOpen = (): void => {
    if (isProxyMode) {
      setErrorMessage(PROXY_RESTRICTED_OPERATION_MESSAGE);
      return;
    }
    setOcrCreditDialogOpen(true);
  };

  const handleOcrCreditClose = (): void => {
    setOcrCreditDialogOpen(false);
  };

  // お問い合わせ処理
  const handleContact = (category: string, type?: string): void => {
    const params = new URLSearchParams({ category });
    if (type) {
      params.append('type', type);
    }
    navigate(`/contact?${params.toString()}`);
  };

  // エラーSnackbar閉じる
  const handleErrorSnackbarClose = (): void => {
    setErrorMessage('');
  };

  // OCR利用状況の表示文言（#3146）。サイドバーと同じユーティリティで生成する
  const ocrUsageDisplay = buildOcrUsageDisplay({
    ocrMonthlyBudget: companyData?.ocr_monthly_budget ?? 0,
    currentMonthOcrCost: companyData?.current_month_ocr_cost ?? 0,
    currentMonthOcrCount: companyData?.current_month_ocr_count ?? 0,
    ocrCreditBalance: companyData?.ocr_credit_balance ?? 0,
  });

  // 有効期限表示テキストを生成
  // 有効なクレジットがある場合は有効期限・一部期限切れの文言を返し、
  // 残高0円・期限切れでも付与履歴があれば「クレジット履歴」ラベルを返して履歴モーダルへの導線を維持する
  const getExpiryDisplayText = (): string | null => {
    if (!companyData) {
      return null;
    }

    if (expiringCredit && companyData.ocr_credit_balance > 0) {
      // remaining_amountの合計がocr_credit_balanceを超えないようクランプ
      const displayAmount = Math.min(expiringCredit.totalAmount, companyData.ocr_credit_balance);

      if (companyData.ocr_credit_balance === displayAmount) {
        return `有効期限: ${formatDate(expiringCredit.expiresAt)}`;
      }

      return `うち${displayAmount.toLocaleString()}円が ${formatDate(expiringCredit.expiresAt)} に期限切れ`;
    }

    if (hasCreditGrants) {
      return 'クレジット履歴';
    }

    return null;
  };

  // 成功Snackbar閉じる
  const handleSuccessSnackbarClose = (): void => {
    setSuccessMessage('');
  };

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>

      <Box
        sx={[
          layoutStyles.pageContent,
          sideBarCollapsed && layoutStyles.pageContentCollapsed,
          {
            '@media screen and (max-width:960px)': {
              paddingBottom: '40px',
            },
          },
        ]}
      >
        <Box
          sx={{
            ...layoutStyles.header,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography sx={styles.headerTitle}>{ACCOUNT_MANAGEMENT_TITLE}</Typography>
          {!isOperatorStaff(user?.role) && <ReferralInvitationCard compact />}
        </Box>

        {isLoading || !companyData ? (
          <CustomLoading loading={true} />
        ) : (
          <>
            {/* プラン情報 */}
            <Box sx={styles.section}>
              <Typography sx={styles.sectionTitle}>プラン情報</Typography>
              <Box sx={styles.infoRow}>
                <Typography sx={styles.infoLabel}>契約中のプラン</Typography>
                <Box sx={styles.infoValue}>
                  <Typography sx={styles.infoValueText}>{getPlanName(companyData.plan)}</Typography>
                  <Button sx={styles.changeButton} onClick={handlePlanChangeOpen}>
                    変更する
                  </Button>
                </Box>
              </Box>
              <Box sx={styles.infoRow}>
                <Typography sx={styles.infoLabel}>ユーザー数</Typography>
                <Box sx={styles.infoValueWithGap86}>
                  <Box>
                    <Typography sx={styles.infoValueText}>
                      利用ユーザー数 {companyData.current_user_count}名
                    </Typography>
                    <Typography sx={styles.infoValueText}>
                      最大ユーザー数 {companyData.user_limit}名
                    </Typography>
                  </Box>
                  <Button sx={styles.changeButton} onClick={handleUserCountChangeOpen}>
                    変更する
                  </Button>
                </Box>
              </Box>
              <Box sx={styles.infoRow}>
                <Typography sx={styles.infoLabel}>支払いサイクル</Typography>
                <Box sx={styles.infoValue}>
                  <Typography sx={styles.infoValueText}>
                    {getPaymentCycleName(companyData.payment_cycle)}
                  </Typography>
                </Box>
              </Box>
              {companyData.enable_master && (
                <Box sx={styles.infoRow}>
                  <Typography sx={styles.infoLabel}>マスタ管理機能</Typography>
                  <Box sx={styles.infoValue}>
                    <Typography sx={styles.infoValueText}>有効</Typography>
                  </Box>
                </Box>
              )}
              {companyData.enable_dictionary && (
                <Box sx={styles.infoRow}>
                  <Typography sx={styles.infoLabel}>辞書変換機能</Typography>
                  <Box sx={styles.infoValue}>
                    <Typography sx={styles.infoValueText}>有効</Typography>
                  </Box>
                </Box>
              )}
              <Box sx={styles.infoRowFlexStart}>
                <Box sx={styles.infoLabel}>
                  <Typography>OCR利用状況</Typography>
                  <HelpOutlineIcon
                    sx={styles.helpIcon}
                    onClick={(): void => {
                      window.open(HELP_FAQ_OCR_USAGE, '_blank', 'noopener,noreferrer');
                    }}
                  />
                </Box>
                <Box sx={styles.infoValueColumn}>
                  <Box sx={styles.ocrRow}>
                    <Typography sx={styles.ocrLabel}>{OCR_USAGE_MONTHLY_LABEL}</Typography>
                    <Typography sx={styles.ocrValue}>{ocrUsageDisplay.monthlyOcrText}</Typography>
                  </Box>
                  <Box sx={styles.ocrCreditRow}>
                    <Box sx={styles.ocrRowCredit}>
                      <Typography sx={styles.ocrLabel}>{OCR_USAGE_CREDIT_LABEL}</Typography>
                      <Typography
                        sx={{
                          ...styles.ocrValue,
                          ...(ocrUsageDisplay.isNegativeCredit && { color: orange30 }),
                        }}
                      >
                        {ocrUsageDisplay.creditBalanceText}
                        {getExpiryDisplayText() && (
                          <Typography
                            component="span"
                            sx={styles.expiryLink}
                            onClick={(): void => setUsageHistoryModalOpen(true)}
                          >
                            {getExpiryDisplayText()}
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                    <Button sx={styles.addButton} onClick={handleOcrCreditOpen}>
                      追加する
                    </Button>
                  </Box>
                  {ocrUsageDisplay.isNegativeCredit && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        backgroundColor: 'rgba(202, 138, 4, 0.1)',
                        padding: '6px 16px',
                        borderRadius: '4px',
                        marginTop: '8px',
                      }}
                    >
                      <ErrorOutline sx={{ fontSize: '22px', color: '#ca8a04' }} />
                      <Typography
                        sx={{
                          fontSize: '14px',
                          color: '#ca8a04',
                          lineHeight: '1.43',
                          letterSpacing: '0.17px',
                        }}
                      >
                        OCRクレジットを追加してください
                      </Typography>
                    </Box>
                  )}
                  <Box sx={styles.ocrRow}>
                    <Typography sx={styles.ocrLabel}>{OCR_USAGE_MONTHLY_USAGE_LABEL}</Typography>
                    <Typography sx={styles.ocrValue}>{ocrUsageDisplay.monthlyReadText}</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* ログイン情報（代理モード時は非表示） */}
            {!isProxyMode && (
              <Box sx={styles.section}>
                <Typography sx={styles.sectionTitle}>ログイン情報</Typography>
                <Box sx={styles.infoRow}>
                  <Typography sx={styles.infoLabel}>メールアドレス</Typography>
                  <Box sx={styles.infoValue}>
                    <Typography sx={styles.infoValueText}>{userEmail}</Typography>
                  </Box>
                </Box>
                <Box sx={styles.infoRow}>
                  <Typography sx={styles.infoLabel}>パスワード</Typography>
                  <Box sx={styles.infoValueSpaceBetween}>
                    <Typography sx={styles.passwordDisplay}>●●●●●●●●</Typography>
                    <Button
                      sx={styles.editButtonWithMargin}
                      onClick={handlePasswordChange}
                      startIcon={<EditOutlinedIcon sx={{ fontSize: '16px' }} />}
                    >
                      変更
                    </Button>
                  </Box>
                </Box>
                <Box sx={styles.infoRow}>
                  <Typography sx={styles.infoLabel}>二要素認証</Typography>
                  <Box sx={styles.infoValueSpaceBetween}>
                    <MfaSettingRow />
                  </Box>
                </Box>
              </Box>
            )}

            {/* 会社情報 */}
            <Box sx={styles.section}>
              <Typography sx={styles.sectionTitle}>会社情報</Typography>
              <Box sx={styles.infoRow}>
                <Typography sx={styles.infoLabel}>会社名</Typography>
                <Box sx={styles.infoValue}>
                  <Typography sx={styles.infoValueText}>{companyData.company_name}</Typography>
                </Box>
              </Box>
              <Box sx={styles.infoRow}>
                <Typography sx={styles.infoLabel}>電話番号</Typography>
                <Box sx={styles.infoValueSpaceBetween}>
                  <Typography sx={styles.infoValueText}>
                    {companyData.phone_number || '-'}
                  </Typography>
                  <Button
                    sx={styles.editButtonWithMargin}
                    onClick={handleCompanyEdit}
                    startIcon={<EditOutlinedIcon sx={{ fontSize: '16px' }} />}
                  >
                    変更
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* AIモデルの最適化 */}
            <Box sx={styles.section}>
              <Typography sx={styles.sectionTitle}>AIモデルの最適化</Typography>
              <Box sx={styles.switchRow}>
                <Box sx={styles.switchContainer}>
                  <Typography sx={styles.switchLabel}>OCR精度向上の設定（AI学習）</Typography>
                  <Box sx={styles.switchWithLabel}>
                    <Switch
                      checked={modelImprovement}
                      onChange={handleModelImprovementToggle}
                      disabled={
                        user?.role !== userRole.admin &&
                        user?.role !== userRole.operator &&
                        user?.role !== userRole.companyAdmin
                      }
                    />
                    <Typography sx={styles.switchStateText}>
                      {modelImprovement ? 'ON' : 'OFF'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={styles.switchDescriptionPadding}>
                <Typography sx={{ ...regularSm, color: gray80, whiteSpace: 'nowrap' }}>
                  アップロードされた書類やOCR結果を学習し、お客様固有の帳票レイアウトや手書き文字の精度向上に役立てます。
                </Typography>
                <Typography sx={{ ...regularSm, color: gray80, whiteSpace: 'nowrap' }}>
                  <Box component="span" sx={{ fontWeight: 'bold' }}>
                    ・目的は「精度向上」のみ：
                  </Box>
                  弊社のOCRモデルの強化にのみ利用します。
                </Typography>
                <Typography sx={{ ...regularSm, color: gray80, whiteSpace: 'nowrap' }}>
                  <Box component="span" sx={{ fontWeight: 'bold' }}>
                    ・プライバシーの保護：
                  </Box>
                  学習した結果から個人情報が特定されたり、外部へ流出したりすることはありません。
                </Typography>
                <Typography sx={{ ...regularSm, color: gray80, whiteSpace: 'nowrap' }}>
                  <Box component="span" sx={{ fontWeight: 'bold' }}>
                    ・外部AIには学習させません：
                  </Box>
                  OpenAIやGeminiなどの外部AIモデルにデータが学習されることは一切ございませんのでご安心ください。
                </Typography>
              </Box>
            </Box>

            {/* 退会する */}
            <Box sx={styles.withdrawContainer}>
              <Typography sx={styles.withdrawLink} onClick={handleWithdrawOpen}>
                退会する
              </Typography>
            </Box>
          </>
        )}
      </Box>

      {/* ダイアログ */}
      {companyData && (
        <>
          <AccountManagementDialog
            open={planChangeDialogOpen}
            onClose={handlePlanChangeClose}
            onContact={(): void => handleContact('plan', 'plan_change')}
            title="プラン変更のご相談"
            infoText={`契約中のプラン：${getPlanName(companyData.plan)}`}
            descriptionLines={[
              'プラン変更をご希望の場合は、お問い合わせフォームよりご希望のプランや変更時期をお知らせください。',
            ]}
            hideCancelButton={true}
          />
          <AccountManagementDialog
            open={userCountChangeDialogOpen}
            onClose={handleUserCountChangeClose}
            onContact={(): void => handleContact('plan', 'additional_user')}
            title={companyData.plan === companyPlan.free ? '担当者の追加' : '担当者数の変更'}
            infoText={`現在の担当者数：${companyData.current_user_count}名 / 上限数${companyData.user_limit}名`}
            descriptionLines={
              companyData.plan === companyPlan.free
                ? [
                    'フリープランでは担当者は1名までとなっています。\n複数名でご利用いただく場合は、スタンダードプラン以上へのアップグレードをご検討ください。',
                  ]
                : [
                    '担当者数の変更をご希望の場合は、お問い合わせフォームより追加希望人数をお知らせください。',
                  ]
            }
            hideCancelButton={true}
          />
          <AccountManagementDialog
            open={withdrawDialogOpen}
            onClose={handleWithdrawClose}
            onContact={(): void => handleContact('account', 'withdrawal')}
            title="退会のご相談"
            descriptionLines={[
              '退会をご希望の場合は、お問い合わせフォームより退会希望日や理由をお知らせください。',
              '※ 退会されると、読取パターンやOCR処理履歴などのデータが削除されますのでご注意ください。',
            ]}
            hideCancelButton={true}
          />
          <AccountManagementDialog
            open={ocrCreditDialogOpen}
            onClose={handleOcrCreditClose}
            onContact={(): void => handleContact('plan', 'ocr_credit')}
            title="OCRクレジットの追加"
            descriptionLines={
              companyData.plan === companyPlan.free
                ? [
                    'フリープランではOCRクレジットを購入できません。\n月間OCR上限（5枚）を超えてご利用いただく場合は、スタンダードプラン以上へのアップグレードをご検討ください。',
                  ]
                : [
                    'OCRクレジットを追加購入いただけます。\n月間OCR上限を超過した場合、OCRクレジット残高から自動的に消費されます。ご希望の購入金額をお問い合わせフォームよりお知らせください。',
                  ]
            }
            hideCancelButton={true}
          />
        </>
      )}

      {/* エラーメッセージ */}
      <CustomSnackbar
        open={!!errorMessage}
        text={errorMessage}
        closeButton={handleErrorSnackbarClose}
        onClose={handleErrorSnackbarClose}
      />

      {/* 成功メッセージ */}
      <CustomSnackbar
        open={!!successMessage}
        text=""
        infoText={successMessage}
        closeButton={handleSuccessSnackbarClose}
        autoHideDuration={SNACKBAR_AUTO_HIDE_DURATION_SHORT}
        onClose={handleSuccessSnackbarClose}
      />

      <OcrCreditUsageHistoryModal
        open={usageHistoryModalOpen}
        onClose={(): void => setUsageHistoryModalOpen(false)}
        companyId={effectiveCompanyId ?? ''}
        showNote={false}
      />
    </Box>
  );
};

export default CompanyAccount;
